import { subscribeEvents } from '@wellread/api';
import type { Book } from '@wellread/api';
import { useEffect } from 'react';
import { config } from '../config';
import { answerEnded } from '../state/answers';
import { keys, queryClient } from './api';
import { session } from './auth';

const settled = new Set(['awaiting_confirm', 'unanalysed', 'ready', 'paused', 'failed']);

/**
 * Keeps the cache live from /v1/events: ingest progress drives the cover
 * veil and the four passes; a finished grade refreshes its review; a
 * finished round of Explore suggestions refreshes Explore; a conversation
 * that changed anywhere refreshes the recent list and that conversation.
 */
export function useLiveEvents(enabled: boolean): void {
  useEffect(() => {
    if (!enabled) return;
    return subscribeEvents(config.apiUrl, session, {
      open: () => {
        void queryClient.invalidateQueries({ queryKey: keys.books });
        // An answer may have finished while the stream was down.
        void queryClient.invalidateQueries({ queryKey: keys.conversations });
      },
      ingest: (e) => {
        const patch = (b: Book): Book => {
          if (b.id !== e.book_id) return b;
          const now = new Date().toISOString();
          const progress = e.step
            ? [
                ...(b.progress ?? []).filter((p) => p.step !== e.step),
                {
                  step: e.step as Book['progress'][number]['step'],
                  done: e.done ?? 0,
                  total: e.total ?? 0,
                  // The step's own clock: kept from the book as read, started now when first seen.
                  started_at: (b.progress ?? []).find((p) => p.step === e.step)?.started_at ?? now,
                  updated_at: now,
                },
              ]
            : (b.progress ?? []);
          return {
            ...b,
            status: (e.status || b.status) as Book['status'],
            status_detail: e.detail ?? b.status_detail,
            // A PDF's pages are drawn: it can be read on them now, and some chapters read best that way.
            pages: e.pages ?? b.pages,
            as_printed: e.as_printed ?? b.as_printed,
            progress,
            // What the analysis has found so far rides on its progress, so an open page needn't ask again.
            stats: {
              ...b.stats,
              ...(e.candidates === undefined ? {} : { candidates: e.candidates }),
              ...(e.verified === undefined ? {} : { verified: e.verified }),
              ...(e.dropped === undefined ? {} : { dropped: e.dropped }),
            },
          };
        };
        queryClient.setQueryData<{ books: Book[] }>(keys.books, (d) =>
          d ? { books: d.books.map(patch) } : d,
        );
        queryClient.setQueryData<Book>(keys.book(e.book_id), (b) => (b ? patch(b) : b));
        if (settled.has(e.status)) {
          void queryClient.invalidateQueries({ queryKey: keys.books });
          void queryClient.invalidateQueries({ queryKey: keys.book(e.book_id) });
          void queryClient.invalidateQueries({ queryKey: keys.contents(e.book_id) });
          void queryClient.invalidateQueries({ queryKey: keys.stats });
        }
      },
      grade: (e) => void queryClient.invalidateQueries({ queryKey: keys.review(e.review_id) }),
      // A round of Explore suggestions finished (or stopped): fetch it.
      explore: () => void queryClient.invalidateQueries({ queryKey: keys.explore }),
      // A book's themes or a track's map finished (or started) building, or you changed
      // it in Explore: fetch that map's levels again. A level asked for with no track
      // may be any track's, so it goes with every track's event.
      map: (e) =>
        void queryClient.invalidateQueries({
          queryKey: ['map'],
          predicate: ({ queryKey: [, scope, book, , , track] }) =>
            scope === e.scope &&
            (e.scope === 'book'
              ? !e.book_id || book === e.book_id
              : !e.track_id || !track || track === e.track_id),
        }),
      // An answer started or finished on some device, or a conversation was removed.
      conversation: (e) => {
        void queryClient.invalidateQueries({ queryKey: keys.conversations });
        void queryClient.invalidateQueries({ queryKey: keys.conversation(e.id) });
        // One the reader asked for here and has since left: they are told it is ready.
        if (e.status !== 'busy') answerEnded(e.id, e.status);
      },
    });
  }, [enabled]);
}
